import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowLeft, CheckCircle2 } from "lucide-react";
import {
  useForgotPasswordMutation,
  useVerifyOtpMutation,
  useResetPasswordMutation,
} from "../store/api/adminAuthApi";

/* ─── Shared logo ─── */
function Logo() {
  return (
    <div className="flex flex-col items-center mb-8">
      <span className="font-manrope-bold text-[22px] text-gray-900 tracking-[0.2em] uppercase">
        CONTENTOUR
      </span>
      <span className="font-manrope-regular text-[13px] text-gray-400 tracking-[0.35em] uppercase">
        BONAIRE
      </span>
      <div className="flex gap-[3px] mt-1.5">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="w-[8px] h-[3px] bg-primary rounded-full" />
        ))}
      </div>
    </div>
  );
}

/* ─── Step dots ─── */
function StepDots({ current }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-7">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="rounded-full transition-all duration-300"
          style={{
            height: 6,
            width: i === current ? 20 : 6,
            backgroundColor: i <= current ? "#FC8EAC" : "#e5e7eb",
          }}
        />
      ))}
    </div>
  );
}

/* ══════════════════════════════════
   STEP 1 — Forgot Password
══════════════════════════════════ */
function StepForgotPassword({ onNext }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await forgotPassword({ email }).unwrap();
      onNext(email);
    } catch (err) {
      setError(err?.data?.error?.message || "Failed to send OTP. Please try again.");
    }
  };

  return (
    <>
      <StepDots current={0} />
      <div className="mb-6">
        <h1 className="text-[20px] font-manrope-bold text-gray-900 mb-1.5">Forgot password?</h1>
        <p className="text-[12px] font-manrope-regular text-gray-400 leading-relaxed">
          Enter your registered email address and we'll send you a 6-digit verification code.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-[12px] font-manrope-semibold text-gray-600 mb-1.5">
            Email address
          </label>
          <div className="relative">
            <Mail size={15} strokeWidth={1.6} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
            />
          </div>
        </div>
        {error && (
          <p className="text-[12px] font-manrope-regular text-red-500 bg-red-50 px-4 py-2.5 rounded-xl">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 rounded-xl text-white text-[14px] font-manrope-bold hover:opacity-90 disabled:opacity-60 transition-all mt-1 bg-primary"
        >
          {isLoading ? "Sending…" : "Send OTP"}
        </button>
      </form>
    </>
  );
}

/* ══════════════════════════════════
   STEP 2 — Verify OTP
══════════════════════════════════ */
function StepVerifyOtp({ email, onNext }) {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(30);
  const inputRefs = useRef([]);

  const [verifyOtp, { isLoading }] = useVerifyOtpMutation();
  const [forgotPassword, { isLoading: isResending }] = useForgotPasswordMutation();

  useEffect(() => {
    if (resendTimer === 0) return;
    const t = setTimeout(() => setResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    setError("");
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const next = [...otp];
    pasted.split("").forEach((ch, i) => { next[i] = ch; });
    setOtp(next);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length < 6) {
      setError("Please enter the complete 6-digit code.");
      return;
    }
    setError("");
    try {
      const result = await verifyOtp({ email, otp: code }).unwrap();
      onNext(result.data.resetToken);
    } catch (err) {
      setError(err?.data?.error?.message || "Invalid OTP. Please try again.");
    }
  };

  const handleResend = async () => {
    setOtp(["", "", "", "", "", ""]);
    setError("");
    setResendTimer(30);
    inputRefs.current[0]?.focus();
    try {
      await forgotPassword({ email }).unwrap();
    } catch {
      setError("Failed to resend OTP. Please try again.");
    }
  };

  return (
    <>
      <StepDots current={1} />
      <div className="flex justify-center mb-5">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, #1a3a4a18, #FC8EAC28)" }}
        >
          <Mail size={24} strokeWidth={1.6} className="text-primary" />
        </div>
      </div>
      <div className="mb-6 text-center">
        <h1 className="text-[20px] font-manrope-bold text-gray-900 mb-1.5">Verify OTP</h1>
        <p className="text-[12px] font-manrope-regular text-gray-400 leading-relaxed">We sent a 6-digit code to</p>
        <p className="text-[13px] font-manrope-semibold text-gray-700 mt-0.5">{email}</p>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="flex items-center justify-between gap-2">
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
              className={`w-12 h-12 text-center text-[18px] font-manrope-bold rounded-xl border bg-gray-50 focus:outline-none transition-all ${
                digit ? "border-primary text-primary" : "border-gray-100 text-gray-700 focus:border-primary"
              }`}
            />
          ))}
        </div>
        {error && (
          <p className="text-[11px] font-manrope-regular text-red-500 bg-red-50 px-4 py-2.5 rounded-xl text-center">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 rounded-xl text-white text-[14px] font-manrope-bold hover:opacity-90 disabled:opacity-60 transition-all bg-primary"
        >
          {isLoading ? "Verifying…" : "Verify OTP"}
        </button>
        <p className="text-center text-[12px] font-manrope-regular text-gray-400">
          Didn't receive the code?{" "}
          {resendTimer > 0 ? (
            <span className="font-manrope-semibold text-gray-400">Resend in {resendTimer}s</span>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={isResending}
              className="font-manrope-semibold text-primary hover:opacity-75 transition-all disabled:opacity-50"
            >
              {isResending ? "Sending…" : "Resend"}
            </button>
          )}
        </p>
      </form>
    </>
  );
}

/* ══════════════════════════════════
   STEP 3 — Set New Password
══════════════════════════════════ */
function StepSetPassword({ resetToken, onNext }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConf, setShowConf] = useState(false);
  const [error, setError] = useState("");

  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const mismatch = confirm.length > 0 && password !== confirm;
  const valid = password.length >= 8 && password === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setError("");
    try {
      await resetPassword({ resetToken, newPassword: password }).unwrap();
      onNext();
    } catch (err) {
      setError(err?.data?.error?.message || "Failed to reset password. Please try again.");
    }
  };

  return (
    <>
      <StepDots current={2} />
      <div className="mb-6">
        <h1 className="text-[20px] font-manrope-bold text-gray-900 mb-1.5">Set new password</h1>
        <p className="text-[12px] font-manrope-regular text-gray-400 leading-relaxed">
          Your new password must be at least 8 characters.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-[12px] font-manrope-semibold text-gray-600 mb-1.5">New password</label>
          <div className="relative">
            <Lock size={15} strokeWidth={1.6} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type={showPass ? "text" : "password"}
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
            />
            <button type="button" onClick={() => setShowPass((s) => !s)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-all">
              {showPass ? <EyeOff size={15} strokeWidth={1.6} /> : <Eye size={15} strokeWidth={1.6} />}
            </button>
          </div>
        </div>
        <div>
          <label className="block text-[12px] font-manrope-semibold text-gray-600 mb-1.5">Confirm new password</label>
          <div className="relative">
            <Lock size={15} strokeWidth={1.6} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type={showConf ? "text" : "password"}
              placeholder="Confirm your password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className={`w-full pl-10 pr-10 py-3 bg-gray-50 border rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none transition-all ${
                mismatch ? "border-red-300 focus:border-red-400" : "border-gray-100 focus:border-primary"
              }`}
            />
            <button type="button" onClick={() => setShowConf((s) => !s)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-all">
              {showConf ? <EyeOff size={15} strokeWidth={1.6} /> : <Eye size={15} strokeWidth={1.6} />}
            </button>
          </div>
          {mismatch && (
            <p className="text-[11px] font-manrope-regular text-red-400 mt-1 ml-1">Passwords do not match</p>
          )}
        </div>
        {error && (
          <p className="text-[12px] font-manrope-regular text-red-500 bg-red-50 px-4 py-2.5 rounded-xl">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={!valid || isLoading}
          className="w-full py-3 rounded-xl text-white text-[14px] font-manrope-bold hover:opacity-90 disabled:opacity-40 transition-all mt-1 bg-primary"
        >
          {isLoading ? "Resetting…" : "Reset password"}
        </button>
      </form>
    </>
  );
}

/* ══════════════════════════════════
   STEP 4 — Success
══════════════════════════════════ */
function StepSuccess({ onLogin }) {
  return (
    <div className="flex flex-col items-center text-center py-2">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
        style={{ background: "linear-gradient(135deg, #1a3a4a18, #FC8EAC28)" }}
      >
        <CheckCircle2 size={30} strokeWidth={1.6} className="text-primary" />
      </div>
      <h1 className="text-[20px] font-manrope-bold text-gray-900 mb-2">Password reset!</h1>
      <p className="text-[12px] font-manrope-regular text-gray-400 leading-relaxed mb-7 max-w-[260px]">
        Your password has been successfully reset. You can now sign in with your new password.
      </p>
      <button
        onClick={onLogin}
        className="w-full py-3 rounded-xl text-white text-[14px] font-manrope-bold hover:opacity-90 transition-all bg-primary"
      >
        Back to sign in
      </button>
    </div>
  );
}

/* ══════════════════════════════════
   MAIN PAGE
══════════════════════════════════ */
export default function RecoverPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState("");

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <div className="w-full max-w-[420px]">
        <Logo />
        <div className="bg-white rounded-2xl shadow-sm p-8">
          {step === 0 && (
            <StepForgotPassword
              onNext={(e) => { setEmail(e); setStep(1); }}
            />
          )}
          {step === 1 && (
            <StepVerifyOtp
              email={email}
              onNext={(token) => { setResetToken(token); setStep(2); }}
            />
          )}
          {step === 2 && (
            <StepSetPassword
              resetToken={resetToken}
              onNext={() => setStep(3)}
            />
          )}
          {step === 3 && <StepSuccess onLogin={() => navigate("/login")} />}
        </div>

        {step < 3 && (
          <button
            onClick={() => step === 0 ? navigate("/login") : setStep((s) => s - 1)}
            className="flex items-center gap-2 mx-auto mt-5 text-[12px] font-manrope-semibold text-gray-500 hover:text-gray-700 transition-all"
          >
            <ArrowLeft size={13} strokeWidth={2} />
            {step === 0 ? "Back to sign in" : "Go back"}
          </button>
        )}

        <p className="text-center text-[11px] font-manrope-regular text-gray-400 mt-5">
          © {new Date().getFullYear()} Contentour Bonaire. All rights reserved.
        </p>
      </div>
    </div>
  );
}
